/**
 * DepartmentsDashboard — Admin command center
 * Three views matching Stitch "Celestial Ledger" design system:
 *   OVERVIEW    → dashboard/code.html   (KPI bento + completion matrix + mission log)
 *   DEPARTMENTS → departments/code.html (bento card grid per dept with SOP bars)
 *   PROJECTS    → projects_plans/code.html (cross-portal project table)
 *
 * Props: role, onNavigate
 */

import React, { useState, useEffect, useMemo } from "react";
import {
  getFirestore, collection, query, orderBy, onSnapshot, where,
} from "firebase/firestore";
import { getApp } from "firebase/app";
import ExperimentsPage from "./ExperimentsPage";
import DepartmentManager from "./DepartmentManager";

let _db = null;
const DB = () => {
  if (!_db) { try { _db = getFirestore(getApp()); } catch (_) {} }
  return _db;
};

// ── Exact Stitch color tokens ─────────────────────────────────────────────────
const C = {
  bg:            "#0c112e",
  surfLow:       "#141936",   // cards / sidebar
  surf:          "#191d3b",   // hover
  surfLowest:    "#070b28",   // stat pills
  surfHigh:      "#232846",
  surfHighest:   "#2e3351",   // progress track / table head
  primary:       "#89ceff",
  onPrimary:     "#00344d",
  onPriCont:     "#0085be",   // gradient end
  tertiary:      "#4cd6ff",
  tertiCont:     "#00141b",
  onTertiCont:   "#0088a7",
  secondary:     "#c0c4ea",
  secCont:       "#424767",
  onSecCont:     "#b2b6db",
  error:         "#ffb4ab",
  errCont:       "#93000a",
  onErrCont:     "#ffdad6",
  onSurf:        "#dee0ff",
  onSurfVar:     "#c7c5ce",
  outlineVar:    "#46464d",
  outline:       "#919098",
  H: "'Space Grotesk', system-ui, sans-serif",
  B: "'Inter', system-ui, sans-serif",
};

const GRAD = `linear-gradient(135deg, ${C.primary} 0%, ${C.onPriCont} 100%)`;

// ── Our 6 portals mapped to departments ──────────────────────────────────────
const DEPTS = [
  { id:"mp",       label:"Mission Planning",    subtitle:"Orbital Mechanics",    icon:"satellite_alt",   iconColor:C.primary,   path:"/mission_planning",    col:"mission_planning_posts" },
  { id:"obc",      label:"OBC Development",     subtitle:"CubeSat · BOM · Risk", icon:"developer_board", iconColor:C.tertiary,  path:"/obc_development",     col:null,   noFeed:true },
  { id:"embedded", label:"Embedded Systems",    subtitle:"PCB · Firmware · BOM", icon:"memory",          iconColor:"#f59e0b",   path:"/embedded_system",     col:"embedded_posts" },
  { id:"system",   label:"Systems Engineering", subtitle:"CAD · FEA · Budgets",  icon:"hub",             iconColor:C.primary,   path:"/system_engineering",  col:"system_posts" },
  { id:"ops",      label:"Operations",          subtitle:"Ground Ops · Briefs",  icon:"manage_accounts", iconColor:C.primary,   path:"/operations",          col:"operations_posts" },
  { id:"c_suite",  label:"C-Suite",             subtitle:"Directors · Board",   icon:"corporate_fare",  iconColor:"#dc2626",   path:"/c_suite",             col:"c_suite_posts" },
  { id:"payload",  label:"Payloads",            subtitle:"Mass · ICD · Integration", icon:"inventory_2", iconColor:"#0891b2",   path:"/payload",             col:"payload_posts" },
  { id:"pe",       label:"Prompt Engineering",  subtitle:"Quality Reviews",      icon:"psychology",      iconColor:"#8b5cf6",   path:"/prompt-engineering",  col:"prompt_engineering_posts" },
];

// Project status config (matches ProjectsDashboard.jsx STATUS_OPTS)
const STATUSES = [
  { id:"planning",    label:"Planning",    bg:`${C.surfHighest}cc`, text:C.outline },
  { id:"in_progress", label:"In Progress", bg:`${C.primary}1a`,    text:C.primary },
  { id:"review",      label:"Review",      bg:`${C.tertiary}1a`,   text:C.tertiary },
  { id:"completed",   label:"Completed",   bg:`${C.onTertiCont}1a`,text:C.onTertiCont },
  { id:"on_hold",     label:"On Hold",     bg:`${C.error}1a`,      text:C.error },
];
const statusCfg = id => STATUSES.find(s => s.id === id) || STATUSES[0];

// ── Tiny helpers ──────────────────────────────────────────────────────────────
const Sym = ({ n, size=24, color, style={} }) => (
  <span className="material-symbols-outlined"
    style={{ fontSize:size, lineHeight:1, display:"inline-block", color, ...style }}>{n}</span>
);

const fmtDate = ts => {
  if (!ts?.toDate) return "—";
  return ts.toDate().toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"2-digit" });
};
const fmtAgo = ts => {
  if (!ts?.toDate) return "";
  const s = (Date.now() - ts.toDate().getTime()) / 1000;
  if (s < 3600)  return `${Math.round(s/60)}m ago`;
  if (s < 86400) return `${Math.round(s/3600)}h ago`;
  return `${Math.round(s/86400)}d ago`;
};

const deptStatus = (total, approved) => {
  if (total === 0) return { label:"No Data",     bg:C.surfHighest, text:C.outline };
  const p = (approved / total) * 100;
  if (p < 25)  return { label:"Critical",    bg:C.errCont,  text:C.onErrCont };
  if (p < 60)  return { label:"Busy",        bg:C.secCont,  text:C.onSecCont };
  return              { label:"Operational", bg:C.tertiCont,text:C.onTertiCont };
};

// ═══════════════════════════════════════════════════════════
//  TOP NAV BAR  (exact Stitch header)
// ═══════════════════════════════════════════════════════════
function TopBar({ view, setView, totalReports, totalApproved, search, setSearch, isAdmin, onManageDepts }) {
  return (
    <header style={{
      position:"sticky", top:0, zIndex:10,
      background:"rgba(20,25,54,0.92)", backdropFilter:"blur(20px)",
      boxShadow:"0px 24px 48px rgba(0,180,255,0.08)",
      padding:"0 24px", height:64,
      display:"flex", alignItems:"center", justifyContent:"space-between", gap:16,
      borderBottom:`1px solid ${C.outlineVar}1a`,
    }}>
      {/* Left: title + nav tabs */}
      <div style={{ display:"flex", alignItems:"center", gap:24, flexShrink:0 }}>
        <h1 style={{ fontFamily:C.H, fontSize:16, fontWeight:700, color:C.primary,
          margin:0, letterSpacing:"0.05em", whiteSpace:"nowrap" }}>
          SOP Tracker
        </h1>
        <nav style={{ display:"flex", gap:2 }}>
          {[["overview","Overview"],["departments","Departments"],["projects","Projects"],["experiments","Experiments"]].map(([id,label]) => (
            <button key={id} onClick={() => setView(id)} style={{
              background:"none", border:"none", cursor:"pointer",
              fontFamily:C.B, fontSize:11, fontWeight:700, letterSpacing:"0.06em",
              textTransform:"uppercase", padding:"4px 10px", paddingBottom:6,
              color: view===id ? C.primary : "#64748b",
              borderBottom: view===id ? `2px solid ${C.primary}` : "2px solid transparent",
              transition:"color 0.2s, border-color 0.2s", whiteSpace:"nowrap",
            }}>{label}</button>
          ))}
        </nav>
      </div>

      {/* Right: admin button + search + kpis */}
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        {/* Admin: New Department button — always visible in header */}
        {isAdmin && (
          <button onClick={onManageDepts}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px",
              background:`linear-gradient(135deg, ${C.primary}22 0%, ${C.primary}11 100%)`,
              border:`1px solid ${C.primary}55`, borderRadius:6, cursor:"pointer",
              fontFamily:C.B, fontSize:11, fontWeight:700, color:C.primary,
              letterSpacing:"0.06em", whiteSpace:"nowrap",
              boxShadow:`0 0 12px ${C.primary}20`, transition:"all 0.15s",
            }}
            onMouseEnter={e=>{ e.currentTarget.style.background=`${C.primary}33`; e.currentTarget.style.boxShadow=`0 0 20px ${C.primary}40`; }}
            onMouseLeave={e=>{ e.currentTarget.style.background=`linear-gradient(135deg, ${C.primary}22 0%, ${C.primary}11 100%)`; e.currentTarget.style.boxShadow=`0 0 12px ${C.primary}20`; }}>
            <Sym n="add_circle" size={16} color={C.primary} />
            NEW DEPARTMENT
          </button>
        )}
        <div style={{ position:"relative" }}>
          <Sym n="search" size={16} color="#64748b"
            style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)" }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search…"
            style={{ background:C.surf, border:"none", borderRadius:8, outline:"none",
              padding:"7px 14px 7px 32px", fontFamily:C.B, fontSize:12,
              color:C.onSurf, width:160 }} />
        </div>
        <div style={{ display:"flex", gap:16 }}>
          {[{v:totalReports,l:"REPORTS",c:C.primary},{v:totalApproved,l:"APPROVED",c:C.tertiary}].map(k=>(
            <div key={k.l} style={{ textAlign:"center" }}>
              <div style={{ fontFamily:C.H, fontSize:15, fontWeight:700, color:k.c }}>{k.v}</div>
              <div style={{ fontFamily:C.B, fontSize:7, color:"#64748b", letterSpacing:"0.1em" }}>{k.l}</div>
            </div>
          ))}
        </div>
      </div>
    </header>
  );
}

// ═══════════════════════════════════════════════════════════
//  OVERVIEW VIEW  (dashboard/code.html)
// ═══════════════════════════════════════════════════════════
function OverviewView({ postsByDept, projects, isAdmin, dynDepts=[], onManageDepts }) {
  const allPosts    = Object.values(postsByDept).flat();
  const totalRpts   = allPosts.length;
  const approved    = allPosts.filter(p => p.adminApproved).length;
  const pending     = totalRpts - approved;
  const totalProj   = projects.length;
  const overallPct  = totalRpts > 0 ? Math.round((approved/totalRpts)*100) : 0;

  // Recent 6 posts for mission log
  const recentLog = useMemo(() => {
    const all = Object.entries(postsByDept).flatMap(([id, posts]) => {
      const d = DEPTS.find(x => x.id===id);
      return posts.map(p => ({ ...p, _dept:d }));
    });
    return all.sort((a,b) => {
      const ta = a.uploadedAt?.toDate ? a.uploadedAt.toDate() : new Date(0);
      const tb = b.uploadedAt?.toDate ? b.uploadedAt.toDate() : new Date(0);
      return tb - ta;
    }).slice(0,5);
  }, [postsByDept]);

  const kpis = [
    { label:"Total Portals",   val:DEPTS.length,    icon:"account_tree",      tag:{ text:"Active Mission", c:C.tertiary, bg:C.tertiCont } },
    { label:"Total Reports",   val:totalRpts,        icon:"assignment_turned_in", bar:Math.min(overallPct,100) },
    { label:"Pending Reviews", val:pending,          icon:"rate_review",       tag: pending>0 ? { text:"Requires Attention", c:C.error, bg:`${C.errCont}4d` } : { text:"All clear", c:C.tertiary, bg:C.tertiCont } },
    { label:"Approved",        val:totalApproved,    icon:"military_tech",     tag:{ text:`${overallPct}% SOP Rate`, c:C.tertiary, bg:C.tertiCont } },
  ];
  const totalApproved = approved;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:32 }}>
      {/* 4-col KPI bento */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:20 }}>
        {kpis.map(k => (
          <div key={k.label}
            style={{ background:C.surfLow, padding:24, borderRadius:12, position:"relative",
              overflow:"hidden", transition:"background 0.3s", cursor:"default" }}
            onMouseEnter={e=>(e.currentTarget.style.background=C.surf)}
            onMouseLeave={e=>(e.currentTarget.style.background=C.surfLow)}>
            {/* Ghost icon */}
            <Sym n={k.icon} size={72} color={C.primary}
              style={{ position:"absolute", right:-8, bottom:-8, opacity:0.08, pointerEvents:"none" }} />
            <p style={{ fontFamily:C.B, fontSize:9, fontWeight:700, letterSpacing:"0.1em",
              textTransform:"uppercase", color:C.primary, margin:"0 0 4px" }}>{k.label}</p>
            <h3 style={{ fontFamily:C.H, fontSize:36, fontWeight:700, color:C.onSurf,
              margin:"0 0 14px", lineHeight:1 }}>{k.val}</h3>
            {k.bar !== undefined && (
              <div style={{ height:4, background:C.surfHighest, borderRadius:999, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${k.bar}%`, background:GRAD }} />
              </div>
            )}
            {k.tag && (
              <span style={{ fontFamily:C.B, fontSize:9, fontWeight:700, color:k.tag.c,
                background:k.tag.bg, padding:"2px 10px", borderRadius:999 }}>{k.tag.text}</span>
            )}
          </div>
        ))}
      </div>

      {/* 8/4 split */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 320px", gap:32, alignItems:"start" }}>
        {/* Left: completion matrix */}
        <div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
            <h2 style={{ fontFamily:C.H, fontSize:18, fontWeight:700, color:C.onSurf, margin:0 }}>
              Department Completion Matrix
            </h2>
            <span style={{ fontFamily:C.B, fontSize:9, fontWeight:700, color:"#64748b",
              textTransform:"uppercase", letterSpacing:"0.1em" }}>Real-time Telemetry</span>
          </div>
          <div style={{ background:C.surfLow, padding:32, borderRadius:12,
            display:"flex", flexDirection:"column", gap:24 }}>
            {DEPTS.filter(d => !d.noFeed).map(dept => {
              const posts = postsByDept[dept.id] || [];
              const tot = posts.length, app = posts.filter(p=>p.adminApproved).length;
              const pct = tot > 0 ? Math.round((app/tot)*100) : 0;
              const crit = pct < 25;
              return (
                <div key={dept.id}>
                  <div style={{ display:"flex", justifyContent:"space-between",
                    alignItems:"center", marginBottom:6 }}>
                    <span style={{ fontFamily:C.B, fontSize:11, textTransform:"uppercase",
                      letterSpacing:"0.07em", color:C.onSurf }}>{dept.label}</span>
                    <span style={{ fontFamily:C.B, fontSize:11, fontWeight:700,
                      color: crit ? C.error : C.primary }}>{pct}%</span>
                  </div>
                  <div style={{ height:6, background:C.surfHighest, borderRadius:999, overflow:"hidden" }}>
                    <div style={{
                      height:"100%", width:`${pct}%`, borderRadius:999, transition:"width 0.8s ease",
                      background: crit ? C.error : GRAD,
                      boxShadow: pct>=60 ? "0 0 8px rgba(137,206,255,0.4)" : "none",
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Deadlines + Mission Log */}
        <div style={{ display:"flex", flexDirection:"column", gap:28 }}>
          {/* Upcoming deadlines (pulled from projects with due dates) */}
          <section>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
              <h2 style={{ fontFamily:C.H, fontSize:12, fontWeight:700, color:C.onSurf,
                margin:0, textTransform:"uppercase", letterSpacing:"0.1em" }}>Deadlines</h2>
              <Sym n="event" size={16} color={C.primary} />
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {projects.filter(p => p.dueDate).slice(0,3).length > 0
                ? projects.filter(p => p.dueDate).slice(0,3).map(proj => {
                    const sc = statusCfg(proj.status);
                    return (
                      <div key={proj.id}
                        style={{ background:C.surfLow, padding:"12px 14px", borderRadius:8,
                          display:"flex", alignItems:"center", gap:12, transition:"background 0.2s" }}
                        onMouseEnter={e=>(e.currentTarget.style.background=C.surf)}
                        onMouseLeave={e=>(e.currentTarget.style.background=C.surfLow)}>
                        <div style={{ width:36, height:36, borderRadius:8, flexShrink:0,
                          background:`${sc.text}1a`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                          <Sym n="schedule" size={18} color={sc.text} />
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontFamily:C.H, fontSize:12, fontWeight:700, color:C.onSurf,
                            overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{proj.title}</div>
                          <div style={{ fontFamily:C.B, fontSize:9, color:"#64748b", marginTop:2 }}>
                            Due {fmtDate(proj.dueDate)} · {proj.department}
                          </div>
                        </div>
                      </div>
                    );
                  })
                : (
                  [
                    { icon:"priority_high", c:C.error, title:"No urgent deadlines", sub:"All projects on track" },
                  ].map((d,i) => (
                    <div key={i} style={{ background:C.surfLow, padding:"12px 14px", borderRadius:8,
                      display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ width:36, height:36, borderRadius:8,
                        background:`${d.c}1a`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <Sym n={d.icon} size={18} color={d.c} />
                      </div>
                      <div>
                        <div style={{ fontFamily:C.H, fontSize:12, fontWeight:700, color:C.onSurf }}>{d.title}</div>
                        <div style={{ fontFamily:C.B, fontSize:9, color:"#64748b", marginTop:2 }}>{d.sub}</div>
                      </div>
                    </div>
                  ))
                )
              }
            </div>
          </section>

          {/* Mission Log */}
          <section>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
              <h2 style={{ fontFamily:C.H, fontSize:12, fontWeight:700, color:C.onSurf,
                margin:0, textTransform:"uppercase", letterSpacing:"0.1em" }}>Mission Log</h2>
              <Sym n="history" size={16} color={C.primary} />
            </div>
            <div style={{ background:C.surfLow, padding:24, borderRadius:12,
              position:"relative", display:"flex", flexDirection:"column", gap:22, overflow:"hidden" }}>
              {/* timeline line */}
              <div style={{ position:"absolute", left:32, top:24, bottom:24, width:1,
                background:C.outlineVar, opacity:0.2 }} />
              {recentLog.length === 0
                ? <p style={{ fontFamily:C.B, fontSize:11, color:"#64748b", paddingLeft:28 }}>No activity yet.</p>
                : recentLog.map((post, i) => {
                    const dot = i===0 ? C.primary : i===1 ? C.tertiary : `${C.primary}66`;
                    const glow = i===0 ? `0 0 8px ${C.primary}` : i===1 ? `0 0 8px ${C.tertiary}` : "none";
                    return (
                      <div key={post.id} style={{ position:"relative", paddingLeft:28, display:"flex", gap:12 }}>
                        <div style={{ position:"absolute", left:-4, top:5, width:9, height:9,
                          borderRadius:"50%", background:dot, boxShadow:glow, flexShrink:0 }} />
                        <div>
                          <p style={{ fontFamily:C.B, fontSize:11, color:C.onSurf, margin:0, lineHeight:1.4 }}>
                            {post.adminApproved ? "Approved: " : "New report: "}
                            <span style={{ color: post.adminApproved ? C.tertiary : C.primary }}>
                              {post.title}
                            </span>
                          </p>
                          <p style={{ fontFamily:C.B, fontSize:9, color:"#64748b", margin:"3px 0 0" }}>
                            {fmtAgo(post.uploadedAt)} · {post._dept?.label || ""}
                          </p>
                        </div>
                      </div>
                    );
                  })}
            </div>
          </section>
        </div>
      </div>

      {/* Custom Departments Strip */}
      {(isAdmin || dynDepts.length > 0) && (
        <div style={{ marginTop:32, paddingTop:28, borderTop:`1px solid ${C.outlineVar}1a` }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
            <Sym n="add_circle" size={18} color={C.primary} />
            <h2 style={{ fontFamily:C.H, fontSize:16, fontWeight:700, color:C.onSurf, margin:0 }}>
              Custom Departments
            </h2>
            {isAdmin && (
              <button onClick={onManageDepts}
                style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:6,
                  padding:"6px 16px", background:`${C.primary}22`,
                  border:`1px solid ${C.primary}55`, borderRadius:6, cursor:"pointer",
                  fontFamily:C.B, fontSize:10, fontWeight:700, color:C.primary, letterSpacing:"0.06em" }}>
                <Sym n="settings" size={14} color={C.primary} />
                MANAGE
              </button>
            )}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:16 }}>
            {dynDepts.map(dept => (
              <div key={dept.id} onClick={() => window.open(`/dept/${dept.id}`, '_self')}
                style={{ background:C.surfLow, border:`1px solid ${dept.color||C.primary}44`,
                  borderRadius:10, padding:20, cursor:'pointer', position:'relative', overflow:'hidden',
                  transition:'background 0.2s, transform 0.15s' }}
                onMouseEnter={e=>{ e.currentTarget.style.background=C.surf; e.currentTarget.style.transform='translateY(-2px)'; }}
                onMouseLeave={e=>{ e.currentTarget.style.background=C.surfLow; e.currentTarget.style.transform='none'; }}>
                <div style={{ position:'absolute', right:-8, bottom:-8, opacity:0.08 }}>
                  <Sym n={dept.msIcon||'folder_special'} size={64} color={dept.color||C.primary} />
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                  <div style={{ width:36, height:36, borderRadius:8, background:`${dept.color||C.primary}22`,
                    border:`1px solid ${dept.color||C.primary}44`,
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <Sym n={dept.msIcon||'folder_special'} size={20} color={dept.color||C.primary} />
                  </div>
                  <div>
                    <div style={{ fontFamily:C.H, fontSize:13, fontWeight:700, color:C.onSurf, lineHeight:1.2 }}>{dept.name}</div>
                    {dept.subtitle && <div style={{ fontFamily:C.B, fontSize:9, color:C.onSurfVar }}>{dept.subtitle}</div>}
                  </div>
                </div>
                {(dept.categories||[]).length > 0 && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:10 }}>
                    {dept.categories.slice(0,3).map(c => (
                      <span key={c.id} style={{ fontFamily:C.B, fontSize:8, fontWeight:600,
                        color:`${dept.color||C.primary}cc`, background:`${dept.color||C.primary}18`,
                        padding:'2px 7px', borderRadius:4 }}>{c.label}</span>
                    ))}
                  </div>
                )}
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <div style={{ width:5, height:5, borderRadius:'50%', background: dept.color||C.primary }} />
                  <span style={{ fontFamily:C.B, fontSize:9, color:C.onSurfVar, letterSpacing:'0.05em' }}>OPEN PORTAL →</span>
                </div>
              </div>
            ))}
            {/* Add new dept card (admin only) */}
            {isAdmin && (
              <div onClick={onManageDepts}
                style={{ background:'transparent', border:`2px dashed ${C.outlineVar}55`,
                  borderRadius:10, padding:20, cursor:'pointer', minHeight:130,
                  display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:10,
                  transition:'border-color 0.2s' }}
                onMouseEnter={e=>e.currentTarget.style.borderColor=C.primary}
                onMouseLeave={e=>e.currentTarget.style.borderColor=`${C.outlineVar}55`}>
                <Sym n="add_circle" size={28} color={C.outlineVar} />
                <span style={{ fontFamily:C.B, fontSize:10, fontWeight:700, color:C.outlineVar, letterSpacing:"0.1em", textTransform:'uppercase', textAlign:'center' }}>
                  New Department
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer style={{ marginTop:24, paddingTop:24, paddingBottom:12,
        borderTop:`1px solid ${C.outlineVar}1a`,
        display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontFamily:C.B, fontSize:9, fontWeight:700, letterSpacing:"0.1em",
            textTransform:"uppercase", color:"#64748b" }}>System Status: Nominal</span>
          <div style={{ width:6, height:6, borderRadius:"50%", background:C.tertiary,
            animation:"pulse 2s infinite" }} />
        </div>
        <span style={{ fontFamily:C.B, fontSize:9, fontWeight:700, letterSpacing:"0.1em",
          textTransform:"uppercase", color:"#64748b" }}>
          Station: Spacemancraft Heavy Industrial · v{process.env.REACT_APP_VERSION || '0.3.8'}
        </span>
      </footer>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  SINGLE DEPT CARD  (departments/code.html card)
// ═══════════════════════════════════════════════════════════
function DeptCard({ dept, posts }) {
  const [hov, setHov] = useState(false);
  const total = posts.length;
  const approved = posts.filter(p => p.adminApproved).length;
  const pending = total - approved;
  const pct = total > 0 ? Math.round((approved / total) * 100) : 0;
  const status = deptStatus(total, approved);
  const pctColor = pct < 25 ? C.error : pct < 60 ? C.secondary : C.primary;
  const barFill  = pct < 25 ? C.error : GRAD;
  const latest = posts[0]?.uploadedBy || "—";

  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? C.surf : C.surfLow,
        borderRadius:12, padding:24,
        borderBottom:`1px solid ${C.outlineVar}0d`,
        borderRight: `1px solid ${C.outlineVar}0d`,
        transition:"background 0.3s",
        display:"flex", flexDirection:"column",
      }}>

      {/* Header row: icon + name + status */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28 }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ padding:12, background:`${dept.iconColor}1a`, borderRadius:8,
            display:"flex", alignItems:"center", justifyContent:"center" }}>
            <Sym n={dept.icon} size={28} color={dept.iconColor} />
          </div>
          <div>
            <h3 style={{ fontFamily:C.H, fontSize:17, fontWeight:700, color:C.onSurf, margin:0, lineHeight:1.2 }}>
              {dept.label}
            </h3>
            <p style={{ fontFamily:C.B, fontSize:10, color:"#64748b", margin:"3px 0 0",
              textTransform:"uppercase", letterSpacing:"0.05em" }}>{dept.subtitle}</p>
          </div>
        </div>
        <span style={{ padding:"4px 12px", borderRadius:999, background:status.bg, color:status.text,
          fontFamily:C.B, fontSize:9, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase",
          whiteSpace:"nowrap", flexShrink:0 }}>{status.label}</span>
      </div>

      {/* Body */}
      <div style={{ display:"flex", flexDirection:"column", gap:18, flex:1 }}>
        {dept.noFeed ? (
          /* OBC: no feed */
          <div style={{ background:C.surfLowest, padding:"18px 14px", borderRadius:8,
            display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
            <Sym n="open_in_new" size={15} color={dept.iconColor} />
            <a href={dept.path} style={{ fontFamily:C.B, fontSize:11, fontWeight:700,
              color:dept.iconColor, textDecoration:"none", letterSpacing:"0.05em", textTransform:"uppercase" }}>
              Open Dashboard
            </a>
          </div>
        ) : (
          <>
            {/* Head row — repurposed to show latest reporter */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontFamily:C.B, fontSize:13, color:C.onSurfVar }}>Latest Upload</span>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ width:22, height:22, borderRadius:"50%", background:`${dept.iconColor}22`,
                  display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <Sym n="person" size={13} color={dept.iconColor} />
                </div>
                <span style={{ fontFamily:C.B, fontSize:12, fontWeight:500, color:C.onSurf,
                  maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {latest}
                </span>
              </div>
            </div>

            {/* 2 stat pills */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div style={{ background:C.surfLowest, padding:"12px 14px", borderRadius:8 }}>
                <p style={{ fontFamily:C.B, fontSize:9, fontWeight:700, color:"#64748b",
                  textTransform:"uppercase", letterSpacing:"0.06em", margin:"0 0 4px" }}>Total Reports</p>
                <p style={{ fontFamily:C.H, fontSize:26, fontWeight:700, color:C.primary, margin:0, lineHeight:1 }}>
                  {total}
                </p>
              </div>
              <div style={{ background:C.surfLowest, padding:"12px 14px", borderRadius:8 }}>
                <p style={{ fontFamily:C.B, fontSize:9, fontWeight:700, color:"#64748b",
                  textTransform:"uppercase", letterSpacing:"0.06em", margin:"0 0 4px" }}>
                  {pending > 0 ? "Pending" : "Approved"}
                </p>
                <p style={{ fontFamily:C.H, fontSize:26, fontWeight:700,
                  color: pending > 0 ? C.secondary : C.tertiary, margin:0, lineHeight:1 }}>
                  {pending > 0 ? pending : approved}
                </p>
              </div>
            </div>

            {/* SOP completion bar */}
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:6 }}>
                <span style={{ fontFamily:C.B, fontSize:9, fontWeight:700, color:"#64748b",
                  textTransform:"uppercase", letterSpacing:"0.1em" }}>SOP Completion</span>
                <span style={{ fontFamily:C.H, fontSize:13, fontWeight:700, color:pctColor }}>{pct}%</span>
              </div>
              <div style={{ height:6, background:C.surfHighest, borderRadius:999, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${pct}%`, background:barFill, borderRadius:999,
                  boxShadow: pct>=60 ? "0 0 8px rgba(137,206,255,0.4)" : "none",
                  transition:"width 0.7s ease" }} />
              </div>
            </div>
          </>
        )}

        {/* Open Portal link */}
        <a href={dept.path} style={{ display:"inline-flex", alignItems:"center", gap:6,
          padding:"7px 14px", borderRadius:6, border:`1px solid ${C.outlineVar}26`,
          background:"transparent", color:C.primary, fontFamily:C.B,
          fontSize:11, fontWeight:700, letterSpacing:"0.07em", textTransform:"uppercase",
          textDecoration:"none", alignSelf:"flex-end", marginTop:"auto", transition:"background 0.2s" }}
          onMouseEnter={e=>(e.currentTarget.style.background=`${C.primary}0d`)}
          onMouseLeave={e=>(e.currentTarget.style.background="transparent")}>
          Open Portal <Sym n="arrow_forward" size={12} color={C.primary} />
        </a>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  DEPARTMENTS VIEW  (departments/code.html)
// ═══════════════════════════════════════════════════════════
function DeptsView({ postsByDept, search, onExport }) {
  const filtered = search.trim()
    ? DEPTS.filter(d => d.label.toLowerCase().includes(search.toLowerCase()))
    : DEPTS;

  return (
    <div>
      {/* Page heading */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end",
        marginBottom:36, flexWrap:"wrap", gap:16 }}>
        <div>
          <span style={{ fontFamily:C.B, fontSize:9, fontWeight:700, letterSpacing:"0.1em",
            textTransform:"uppercase", color:C.tertiary, display:"block", marginBottom:6 }}>
            Fleet Management
          </span>
          <h2 style={{ fontFamily:C.H, fontSize:44, fontWeight:700, margin:0,
            lineHeight:1.05, letterSpacing:"-0.01em", color:C.onSurf }}>Departments</h2>
        </div>
        <div style={{ display:"flex", gap:12 }}>
          <button onClick={onExport} style={{
            padding:"10px 22px", borderRadius:8, border:`1px solid ${C.outlineVar}26`,
            background:"transparent", color:C.primary, fontFamily:C.B,
            fontSize:11, fontWeight:700, letterSpacing:"0.06em", cursor:"pointer",
            transition:"background 0.2s" }}
            onMouseEnter={e=>(e.currentTarget.style.background=`${C.primary}0d`)}
            onMouseLeave={e=>(e.currentTarget.style.background="transparent")}>
            EXPORT LEDGER
          </button>
          <button style={{ padding:"10px 22px", borderRadius:8, background:GRAD, border:"none",
            color:C.onPrimary, fontFamily:C.B, fontSize:11, fontWeight:700, letterSpacing:"0.06em",
            cursor:"pointer", display:"flex", alignItems:"center", gap:8,
            boxShadow:`0 8px 24px ${C.primary}33`, transition:"transform 0.1s" }}
            onMouseEnter={e=>(e.currentTarget.style.transform="scale(0.97)")}
            onMouseLeave={e=>(e.currentTarget.style.transform="scale(1)")}>
            <Sym n="add" size={14} color={C.onPrimary} /> INITIALIZE DEPT
          </button>
        </div>
      </div>

      {/* 3-col bento grid */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))", gap:28 }}>
        {filtered.map(dept => (
          <DeptCard key={dept.id} dept={dept} posts={postsByDept[dept.id] || []} />
        ))}
        {filtered.length === 0 && (
          <div style={{ gridColumn:"1/-1", padding:"80px 0", textAlign:"center",
            fontFamily:C.B, color:"#64748b" }}>No departments match "{search}"</div>
        )}
      </div>

      {/* Footer */}
      <footer style={{ marginTop:48, paddingTop:20, paddingBottom:8,
        borderTop:`1px solid ${C.outlineVar}0d`,
        display:"flex", justifyContent:"space-between", alignItems:"center", opacity:0.4 }}>
        <span style={{ fontFamily:C.B, fontSize:9, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase" }}>
          Satellite Tracking ID: SC-SO-021-0
        </span>
        <span style={{ fontFamily:C.B, fontSize:9, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase" }}>
          Last Ledger Update: {new Date().toUTCString().slice(17,25)} UTC
        </span>
      </footer>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  PROJECTS VIEW  (projects_plans/code.html)
// ═══════════════════════════════════════════════════════════
function ProjectsView({ projects, postsByDept }) {
  const [deptFilter,   setDeptFilter]   = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page,         setPage]         = useState(0);
  const PER = 8;

  const allPosts   = Object.values(postsByDept).flat();
  const approvalRate = allPosts.length > 0
    ? Math.round((allPosts.filter(p=>p.adminApproved).length / allPosts.length) * 100) : 0;

  const deptNames = [...new Set(projects.map(p => p.department).filter(Boolean))];

  const filtered = projects.filter(p => {
    if (deptFilter !== "all" && p.department !== deptFilter) return false;
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    return true;
  });

  const totalPages = Math.ceil(filtered.length / PER);
  const page_items = filtered.slice(page * PER, (page + 1) * PER);

  const upcomingDeadlines = projects.filter(p => p.dueDate && p.status !== "completed").length;

  const dotColor = { planning:"#919098", in_progress:C.primary, review:C.tertiary,
    completed:C.onTertiCont, on_hold:C.error };

  return (
    <div>
      {/* Hero stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:20, marginBottom:40 }}>
        {[
          { label:"Active Missions",    val:projects.filter(p=>p.status==="in_progress").length,
            icon:"rocket", c:C.primary, sub:`${projects.length} total` },
          { label:"Completion Rate",    val:`${approvalRate}%`,
            icon:"verified", c:C.primary, bar:approvalRate },
          { label:"Upcoming Deadlines", val:upcomingDeadlines,
            icon:"warning", c:C.error, sub:"Requires command attention", italic:true },
        ].map(k => (
          <div key={k.label}
            style={{ background:C.surfLow, padding:24, borderRadius:12, position:"relative",
              overflow:"hidden", transition:"background 0.3s" }}
            onMouseEnter={e=>(e.currentTarget.style.background=C.surf)}
            onMouseLeave={e=>(e.currentTarget.style.background=C.surfLow)}>
            <Sym n={k.icon} size={56} color={k.c}
              style={{ position:"absolute", top:8, right:8, opacity:0.1, pointerEvents:"none" }} />
            <p style={{ fontFamily:C.B, fontSize:9, fontWeight:700, letterSpacing:"0.1em",
              textTransform:"uppercase", color:C.outline, margin:"0 0 8px" }}>{k.label}</p>
            <h2 style={{ fontFamily:C.H, fontSize:36, fontWeight:700, color:k.c,
              margin:"0 0 12px", lineHeight:1 }}>{k.val}</h2>
            {k.bar !== undefined && (
              <div style={{ height:4, background:C.surfHighest, borderRadius:999, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${k.bar}%`, background:GRAD }} />
              </div>
            )}
            {k.sub && (
              <p style={{ fontFamily:C.B, fontSize:11, color: k.italic ? `${C.error}99` : C.tertiary,
                margin:0, fontStyle: k.italic ? "italic" : "normal",
                display:"flex", alignItems:"center", gap:4 }}>
                {!k.italic && <Sym n="trending_up" size={12} color={C.tertiary} />}
                {k.sub}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Filters + CTA */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end",
        flexWrap:"wrap", gap:16, marginBottom:24 }}>
        <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
          {[
            { label:"Department", val:deptFilter, set:setDeptFilter,
              opts:[{v:"all",l:"All Divisions"},...deptNames.map(d=>({v:d,l:d}))] },
            { label:"Status", val:statusFilter, set:setStatusFilter,
              opts:[{v:"all",l:"All Statuses"},...STATUSES.map(s=>({v:s.id,l:s.label}))] },
          ].map(f => (
            <div key={f.label} style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label style={{ fontFamily:C.B, fontSize:9, fontWeight:700, letterSpacing:"0.1em",
                textTransform:"uppercase", color:C.outline, paddingLeft:4 }}>{f.label}</label>
              <select value={f.val} onChange={e => { f.set(e.target.value); setPage(0); }}
                style={{ background:C.surfHigh, border:"none",
                  borderBottom:`1px solid ${C.outline}4d`, borderRadius:8,
                  padding:"8px 14px", color:C.onSurf, fontFamily:C.B, fontSize:13,
                  outline:"none", cursor:"pointer", minWidth:150 }}>
                {f.opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
          ))}
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button style={{ padding:"10px 20px", borderRadius:8, background:GRAD, border:"none",
            color:C.onPrimary, fontFamily:C.B, fontSize:11, fontWeight:700, letterSpacing:"0.06em",
            cursor:"pointer", display:"flex", alignItems:"center", gap:8,
            boxShadow:`0 8px 20px ${C.primary}33`, transition:"all 0.2s" }}
            onMouseEnter={e=>(e.currentTarget.style.boxShadow=`0 0 15px ${C.primary}66`)}
            onMouseLeave={e=>(e.currentTarget.style.boxShadow=`0 8px 20px ${C.primary}33`)}>
            <Sym n="add" size={14} color={C.onPrimary} /> Initiate New Project
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ background:C.surfLow, borderRadius:12, overflow:"hidden",
        border:`1px solid ${C.outlineVar}1a` }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:`${C.surfHigh}80`, borderBottom:`1px solid ${C.outlineVar}1a` }}>
              {["Project Name","Department","Owner","Timeline","Status","Actions"].map((h,i) => (
                <th key={h} style={{ padding:"14px 20px", fontFamily:C.B, fontSize:9, fontWeight:700,
                  letterSpacing:"0.1em", textTransform:"uppercase", color:C.outline,
                  textAlign: i>=4 ? "center" : "left", whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {page_items.length === 0 ? (
              <tr><td colSpan={6} style={{ padding:"40px 20px", textAlign:"center",
                fontFamily:C.B, color:"#64748b" }}>No projects found.</td></tr>
            ) : page_items.map(proj => {
              const sc = statusCfg(proj.status || "planning");
              const dc = dotColor[proj.status] || C.outline;
              return (
                <tr key={proj.id}
                  style={{ borderBottom:`1px solid ${C.outlineVar}08`, transition:"background 0.2s" }}
                  onMouseEnter={e=>(e.currentTarget.style.background=`${C.surf}80`)}
                  onMouseLeave={e=>(e.currentTarget.style.background="transparent")}>
                  {/* Project name */}
                  <td style={{ padding:"18px 20px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:dc, flexShrink:0,
                        boxShadow: proj.status==="in_progress" ? `0 0 6px ${dc}` : "none" }} />
                      <span style={{ fontFamily:C.H, fontSize:13, fontWeight:500, color:C.onSurf }}>
                        {proj.title}
                      </span>
                    </div>
                  </td>
                  {/* Department */}
                  <td style={{ padding:"18px 20px" }}>
                    <span style={{ fontFamily:C.B, fontSize:11,
                      background:`${C.secCont}4d`, color:C.secondary,
                      padding:"3px 10px", borderRadius:999, border:`1px solid ${C.secondary}1a` }}>
                      {proj.department || "—"}
                    </span>
                  </td>
                  {/* Owner */}
                  <td style={{ padding:"18px 20px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ width:24, height:24, borderRadius:"50%", background:`${C.primary}1a`,
                        display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        <Sym n="person" size={13} color={C.primary} />
                      </div>
                      <span style={{ fontFamily:C.B, fontSize:12, color:C.onSurf,
                        maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {proj.createdBy || "—"}
                      </span>
                    </div>
                  </td>
                  {/* Timeline */}
                  <td style={{ padding:"18px 20px" }}>
                    <div style={{ display:"flex", flexDirection:"column" }}>
                      <span style={{ fontFamily:C.B, fontSize:11, color:`${C.onSurf}cc` }}>
                        {fmtDate(proj.createdAt)}
                      </span>
                      {proj.dueDate && (
                        <span style={{ fontFamily:C.B, fontSize:9, color:C.outline }}>
                          Due: {fmtDate(proj.dueDate)}
                        </span>
                      )}
                    </div>
                  </td>
                  {/* Status */}
                  <td style={{ padding:"18px 20px", textAlign:"center" }}>
                    <span style={{ fontFamily:C.B, fontSize:9, fontWeight:700, letterSpacing:"0.1em",
                      textTransform:"uppercase", background:sc.bg, color:sc.text,
                      padding:"4px 12px", borderRadius:999 }}>{sc.label}</span>
                  </td>
                  {/* Actions */}
                  <td style={{ padding:"18px 20px", textAlign:"center" }}>
                    <button style={{ background:"none", border:"none", cursor:"pointer",
                      color:C.outline, transition:"color 0.2s", padding:4 }}
                      onMouseEnter={e=>(e.currentTarget.style.color=C.primary)}
                      onMouseLeave={e=>(e.currentTarget.style.color=C.outline)}>
                      <Sym n="more_vert" size={20} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ marginTop:20, display:"flex", justifyContent:"space-between",
        alignItems:"center", flexWrap:"wrap", gap:12 }}>
        <span style={{ fontFamily:C.B, fontSize:9, fontWeight:700, letterSpacing:"0.1em",
          textTransform:"uppercase", color:C.outline }}>
          Displaying {page_items.length} of {filtered.length} Projects
        </span>
        <div style={{ display:"flex", gap:6 }}>
          {[
            { label:"Prev", disabled:page===0, onClick:()=>setPage(p=>p-1) },
            ...Array.from({length:Math.min(totalPages,5)}, (_,i) => ({
              label:`${i+1}`, disabled:false, active:i===page, onClick:()=>setPage(i)
            })),
            { label:"Next", disabled:page>=totalPages-1, onClick:()=>setPage(p=>p+1) },
          ].map((b,i) => (
            <button key={i} disabled={b.disabled} onClick={b.onClick}
              style={{ background: b.active ? C.surf : C.surfHigh,
                border: b.active ? `1px solid ${C.primary}44` : "none",
                color: b.active ? C.primary : C.outline,
                padding:"6px 12px", borderRadius:6, cursor:b.disabled?"not-allowed":"pointer",
                fontFamily:C.B, fontSize:11, fontWeight:700, opacity:b.disabled?0.3:1,
                transition:"all 0.2s" }}>
              {b.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  ROOT COMPONENT
// ═══════════════════════════════════════════════════════════
export default function DepartmentsDashboard({ role, onNavigate, userId, userName }) {
  const [postsByDept,   setPostsByDept]   = useState({});
  const [projects,      setProjects]      = useState([]);
  const [view,          setView]          = useState("overview");
  const [search,        setSearch]        = useState("");
  const [showDeptMgr,   setShowDeptMgr]   = useState(false);
  const [dynDepts,      setDynDepts]      = useState([]);

  const isAdmin = role === 'admin' || sessionStorage.getItem('sat_admin_session') === '1';

  // Subscribe to all feed dept collections
  useEffect(() => {
    if (!DB()) return;
    const unsubs = DEPTS.filter(d => !d.noFeed && d.col).map(dept => {
      const q = query(collection(DB(), dept.col), orderBy("uploadedAt", "desc"));
      return onSnapshot(q, snap => {
        setPostsByDept(prev => ({
          ...prev,
          [dept.id]: snap.docs.map(d => ({ id:d.id, ...d.data() })),
        }));
      }, () => {});
    });
    return () => unsubs.forEach(u => u());
  }, []);

  // Subscribe to ALL projects (cross-portal)
  useEffect(() => {
    if (!DB()) return;
    const q = query(collection(DB(), "projects"), orderBy("createdAt", "desc"));
    return onSnapshot(q, snap => {
      setProjects(snap.docs.map(d => ({ id:d.id, ...d.data() })));
    }, () => {});
  }, []);

  // Subscribe to dynamic departments
  useEffect(() => {
    if (!DB()) return;
    return onSnapshot(collection(DB(), "departments"), snap => {
      setDynDepts(snap.docs.map(d => ({ id:d.id, ...d.data() })).sort((a,b)=>a.name?.localeCompare(b.name)));
    }, () => {});
  }, []);

  const allPosts     = Object.values(postsByDept).flat();
  const totalReports = allPosts.length;
  const totalApproved = allPosts.filter(p => p.adminApproved).length;

  const handleExport = () => {
    const rows = DEPTS.map(d => {
      const p = postsByDept[d.id] || [];
      const a = p.filter(x=>x.adminApproved).length;
      const pct = p.length > 0 ? Math.round((a/p.length)*100) : 0;
      return `${d.label},${p.length},${a},${p.length-a},${pct}%`;
    });
    const csv = "Department,Total Reports,Approved,Pending,SOP%\n" + rows.join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type:"text/csv" }));
    a.download = `spacemancraft-ledger-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  return (
    <div style={{ background:C.bg, minHeight:"100%", color:C.onSurf, fontFamily:C.B }}>
      {/* Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200');
        .material-symbols-outlined { font-variation-settings:'FILL' 0,'wght' 400,'GRAD' 0,'opsz' 24; display:inline-block; line-height:1; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        select option { background:#1a1f3a; }
      `}</style>

      <TopBar view={view} setView={v=>{ setView(v); }}
        totalReports={totalReports} totalApproved={totalApproved}
        search={search} setSearch={setSearch}
        isAdmin={isAdmin} onManageDepts={()=>setShowDeptMgr(true)} />

      {view === "experiments" ? (
        /* Experiments fills full width with its own header */
        <ExperimentsPage userId={userId} userName={userName} role={role} />
      ) : (
        <div style={{ padding:"40px 32px 60px", maxWidth:1280, margin:"0 auto" }}>
          {view === "overview"    && <OverviewView postsByDept={postsByDept} projects={projects} isAdmin={isAdmin} dynDepts={dynDepts} onManageDepts={()=>setShowDeptMgr(true)} />}
          {view === "departments" && (
            <>
              <DeptsView postsByDept={postsByDept} search={search} onExport={handleExport} />
              {/* Dynamic departments section */}
              {dynDepts.length > 0 && (
                <div style={{ marginTop:40 }}>
                  <div style={{ fontFamily:C.B, fontSize:11, fontWeight:700, letterSpacing:"0.12em",
                    textTransform:"uppercase", color:C.primary, marginBottom:20, display:'flex', alignItems:'center', gap:12 }}>
                    <Sym n="add_circle" size={16} color={C.primary} />
                    CUSTOM DEPARTMENTS
                    {isAdmin && (
                      <button onClick={()=>setShowDeptMgr(true)}
                        style={{ marginLeft:'auto', padding:'5px 14px', fontFamily:C.B, fontSize:9,
                          background:`${C.primary}22`, border:`1px solid ${C.primary}44`,
                          color:C.primary, cursor:'pointer', letterSpacing:1, fontWeight:700 }}>
                        MANAGE
                      </button>
                    )}
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:20 }}>
                    {dynDepts.map(dept => (
                      <div key={dept.id}
                        onClick={() => window.open(`/dept/${dept.id}`, '_self')}
                        style={{ background:C.surfLow, border:`1px solid ${dept.color||C.primary}33`,
                          padding:24, cursor:'pointer', transition:'background 0.2s, transform 0.2s',
                          position:'relative', overflow:'hidden' }}
                        onMouseEnter={e=>{ e.currentTarget.style.background=C.surf; e.currentTarget.style.transform='translateY(-2px)'; }}
                        onMouseLeave={e=>{ e.currentTarget.style.background=C.surfLow; e.currentTarget.style.transform='none'; }}>
                        <div style={{ position:'absolute', right:-10, bottom:-10, opacity:0.07 }}>
                          <Sym n={dept.msIcon||'folder_special'} size={80} color={dept.color||C.primary} />
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                          <div style={{ width:40, height:40, borderRadius:8, background:`${dept.color||C.primary}22`,
                            border:`1px solid ${dept.color||C.primary}44`,
                            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                            <Sym n={dept.msIcon||'folder_special'} size={22} color={dept.color||C.primary} />
                          </div>
                          <div>
                            <div style={{ fontFamily:C.H, fontSize:14, fontWeight:700, color:C.onSurf }}>{dept.name}</div>
                            {dept.subtitle && <div style={{ fontFamily:C.B, fontSize:10, color:C.onSurfVar, marginTop:2 }}>{dept.subtitle}</div>}
                          </div>
                        </div>
                        {dept.description && (
                          <div style={{ fontFamily:C.B, fontSize:11, color:C.onSurfVar, lineHeight:1.5, marginBottom:12,
                            display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                            {dept.description}
                          </div>
                        )}
                        {(dept.categories||[]).length > 0 && (
                          <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                            {dept.categories.slice(0,4).map(c => (
                              <span key={c.id} style={{ fontFamily:C.B, fontSize:8, fontWeight:600,
                                color:`${dept.color||C.primary}cc`, background:`${dept.color||C.primary}18`,
                                padding:'2px 8px', borderRadius:4, letterSpacing:'0.06em' }}>
                                {c.label}
                              </span>
                            ))}
                          </div>
                        )}
                        <div style={{ marginTop:14, display:'flex', alignItems:'center', gap:6 }}>
                          <div style={{ width:6, height:6, borderRadius:'50%', background: dept.color||C.primary }} />
                          <span style={{ fontFamily:C.B, fontSize:9, color:C.onSurfVar, letterSpacing:'0.06em' }}>OPEN PORTAL →</span>
                        </div>
                      </div>
                    ))}
                    {isAdmin && (
                      <div onClick={()=>setShowDeptMgr(true)}
                        style={{ background:'transparent', border:`2px dashed ${C.outlineVar}`,
                          padding:24, cursor:'pointer', display:'flex', flexDirection:'column',
                          alignItems:'center', justifyContent:'center', gap:10, minHeight:160,
                          transition:'border-color 0.2s' }}
                        onMouseEnter={e=>e.currentTarget.style.borderColor=C.primary}
                        onMouseLeave={e=>e.currentTarget.style.borderColor=C.outlineVar}>
                        <Sym n="add_circle" size={32} color={C.outlineVar} />
                        <div style={{ fontFamily:C.B, fontSize:11, fontWeight:700, color:C.outlineVar, letterSpacing:"0.1em", textTransform:'uppercase' }}>
                          Add Department
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {dynDepts.length === 0 && isAdmin && (
                <div style={{ marginTop:40, textAlign:'center' }}>
                  <button onClick={()=>setShowDeptMgr(true)}
                    style={{ padding:'12px 28px', background:`${C.primary}22`, border:`1px dashed ${C.primary}`,
                      color:C.primary, fontFamily:C.B, fontSize:11, fontWeight:700, cursor:'pointer', letterSpacing:1 }}>
                    + CREATE YOUR FIRST CUSTOM DEPARTMENT
                  </button>
                </div>
              )}
            </>
          )}
          {view === "projects"    && <ProjectsView projects={projects} postsByDept={postsByDept} />}
        </div>
      )}

      {/* FAB */}
      <button style={{
        position:"fixed", bottom:32, right:32, width:56, height:56, borderRadius:"50%",
        background:GRAD, border:"none", cursor:"pointer",
        display:"flex", alignItems:"center", justifyContent:"center",
        boxShadow:`0 12px 24px rgba(0,180,255,0.3)`,
        transition:"transform 0.15s", zIndex:50,
      }}
        onMouseEnter={e=>(e.currentTarget.style.transform="scale(1.06)")}
        onMouseLeave={e=>(e.currentTarget.style.transform="scale(1)")}>
        <Sym n="add" size={28} color={C.onPrimary} />
      </button>

      {/* Department Manager modal */}
      {showDeptMgr && (
        <DepartmentManager isAdmin={isAdmin} userId={userId} userName={userName}
          onClose={()=>setShowDeptMgr(false)} />
      )}
    </div>
  );
}
